import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Stack,
  MenuItem,
  TextField,
} from '@mui/material';
import { RootState } from '../../store/store';
import { setDistanceUnit } from '../../store/slices/missionPlannerSlice';
import { useRouteMetrics } from '../../hooks/useRouteMetrics';
import {
  DISTANCE_UNITS,
  DistanceUnit,
  formatDistanceUnit,
} from '../../utils/distance';

/** A labelled metric row in the summary. */
const Metric: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Box>
);

/**
 * Mission summary: total route distance, straight-line distance and route
 * efficiency, plus the distance-unit selector that drives every planner label.
 */
export const MissionSummary: React.FC = () => {
  const dispatch = useDispatch();
  const waypoints = useSelector(
    (state: RootState) => state.missionPlanner.draft.waypoints
  );
  const unit = useSelector(
    (state: RootState) => state.missionPlanner.distanceUnit
  );

  // Includes the loop-closing leg back to home, so "Total distance" is what
  // the drone actually flies (#404).
  const { totalKm, straightLineKm, efficiency } = useRouteMetrics();

  const hasRoute = waypoints.length >= 2;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2" component="h3">
          Summary
        </Typography>
        <TextField
          select
          size="small"
          label="Units"
          value={unit}
          onChange={(e) =>
            dispatch(setDistanceUnit(e.target.value as DistanceUnit))
          }
          sx={{ minWidth: 140 }}
        >
          {DISTANCE_UNITS.map((u) => (
            <MenuItem key={u.value} value={u.value}>
              {u.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {hasRoute ? (
        <Stack spacing={0.5}>
          <Metric label="Total distance" value={formatDistanceUnit(totalKm, unit)} />
          <Metric
            label="Straight-line"
            value={formatDistanceUnit(straightLineKm, unit)}
          />
          <Metric
            label="Efficiency"
            value={`${(efficiency * 100).toFixed(0)}%`}
          />
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Add at least two waypoints to see route distances.
        </Typography>
      )}
    </Box>
  );
};
